import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import type { TimeOption } from "src/components/TimeFilter";
import type {
  StoredDateRange,
  User,
} from "src/interfaces/systemState.interface";
import { asDate } from "src/utils/date";
import {
  calendarYmdInTimeZone,
  endOfCalendarDayContainingInstant,
  getDateTimePartsInTimeZone,
  getDefaultDateRangePeriodForTimeZone,
  getClampStartDateForTimeZone,
  getEffectiveTimeZoneFromUser,
  isYmdString,
  tryYmdStringToEndOfDayUtc,
  tryYmdStringToStartOfDayUtc,
} from "src/utils/timezone";
import { updateDateRange } from "src/store/system/actions";
import type { DateRangeScope } from "src/store/system/actions";
import {
  dashboardDateRangeSelector,
  transactionDateRangeSelector,
} from "src/store/system/selector";

/** Location state shape when navigating to Transaction Details with a date range (e.g. from Sales Analytics). */
export interface DateRangeLocationState {
  /** Preset key; use when navigating with a preset range (day/week/month/year). */
  preset?: "day" | "week" | "month" | "year";
  /** Custom range; use together with endDate when not using a preset. */
  startDate?: Date;
  endDate?: Date;
  view?: string;
}

/**
 * Builds location state for navigation (e.g. to Transaction Details) from a TimeOption.
 * Pass the result as the second argument to history.push(path, state).
 */
export function timeOptionToLocationState(
  option: TimeOption,
  view?: string
): DateRangeLocationState {
  if (option.type === "custom") {
    return {
      startDate: option.period.start,
      endDate: option.period.end,
      view,
    };
  }
  return { preset: option.type, view };
}

type StoredCustomDateRangeShape = {
  start?: unknown;
  end?: unknown;
};

/** One bound: keep `yyyy-mm-dd` as-is; coerce Timestamp / plain object to `Date`. */
function normalizeStoredBound(bound: unknown): string | Date | undefined {
  if (isYmdString(bound)) return bound;
  return asDate(bound);
}

/**
 * Normalizes a stored date range from Firestore: presets are returned as-is;
 * each custom bound is handled independently (`yyyy-mm-dd` preserved; Timestamp → `Date`).
 * Supports mixed storage (e.g. string start + Timestamp end).
 */
export function normalizeStoredDateRange(
  stored: StoredDateRange | undefined
): StoredDateRange | undefined {
  if (stored === undefined || stored === null) return undefined;
  if (typeof stored === "string") return stored;
  const raw = stored as StoredCustomDateRangeShape;
  const start = normalizeStoredBound(raw.start);
  const end = normalizeStoredBound(raw.end);
  if (start === undefined || end === undefined) return undefined;
  return { start, end };
}

/** Map one bound to civil `yyyy-mm-dd` in `timeZone` (string pass-through; Date/Timestamp via calendar day). */
function coerceBoundToYmdString(
  bound: unknown,
  timeZone: string
): string | undefined {
  if (isYmdString(bound)) return bound;
  const d = asDate(bound);
  if (d === undefined) return undefined;
  const ymd = calendarYmdInTimeZone(d, timeZone);
  return ymd || undefined;
}

/**
 * New writes use `yyyy-mm-dd` strings; converts each custom bound to YMD in `timeZone`
 * (Reducer / SET_USER). Legacy `Date` / Timestamp uses the civil day in that zone.
 * Supports mixed string + Timestamp pairs.
 */
export function coerceStoredCustomToYmdStrings(
  stored: StoredDateRange | undefined,
  timeZone: string
): StoredDateRange | undefined {
  if (stored === undefined || stored === null) return undefined;
  if (typeof stored === "string") return stored;
  const raw = stored as StoredCustomDateRangeShape;
  const start = coerceBoundToYmdString(raw.start, timeZone);
  const end = coerceBoundToYmdString(raw.end, timeZone);
  if (start === undefined || end === undefined) return undefined;
  return { start, end };
}

/**
 * One custom-range bound: either `yyyy-mm-dd` (new storage) or a legacy
 * `Date` / Firestore `Timestamp`. Legacy values are interpreted as “which
 * civil day in `timeZone` does this instant fall on?”, then converted to
 * start-of-day or end-of-day UTC for that day.
 */
function storedBoundToUtcInstant(
  bound: unknown,
  timeZone: string,
  edge: "start" | "end"
): Date | undefined {
  if (isYmdString(bound)) {
    return edge === "start"
      ? tryYmdStringToStartOfDayUtc(bound, timeZone)
      : tryYmdStringToEndOfDayUtc(bound, timeZone);
  }
  const d = asDate(bound);
  if (d === undefined) return undefined;
  const ymd = calendarYmdInTimeZone(d, timeZone);
  if (!ymd) return undefined;
  return edge === "start"
    ? tryYmdStringToStartOfDayUtc(ymd, timeZone)
    : tryYmdStringToEndOfDayUtc(ymd, timeZone);
}

function customStoredToPeriodDates(
  stored: StoredCustomDateRangeShape,
  timeZone: string
): { start: Date; end: Date } | undefined {
  const start = storedBoundToUtcInstant(stored.start, timeZone, "start");
  const end = storedBoundToUtcInstant(stored.end, timeZone, "end");
  if (start === undefined || end === undefined) return undefined;
  return { start, end };
}

/**
 * Minimum allowed start date: Jan 1 of prior year in `timeZone`.
 */
export function getClampStartDate(timeZone: string): Date {
  return getClampStartDateForTimeZone(timeZone);
}

/**
 * Safe default period when no value is stored:
 * start = Jan 1 prior year in Jan–Apr, else Jan 1 current year; end = today.
 */
export function getDefaultDateRangePeriod(timeZone: string): {
  start: Date;
  end: Date;
} {
  return getDefaultDateRangePeriodForTimeZone(timeZone);
}

/**
 * Converts a TimeOption (UI) to the value we persist (StoredDateRange).
 * Custom ranges are stored as `yyyy-mm-dd` strings in `timeZone`.
 */
export function toStoredDateRange(
  option: TimeOption,
  timeZone: string
): StoredDateRange {
  if (option.type === "custom") {
    return {
      start: calendarYmdInTimeZone(option.period.start, timeZone),
      end: calendarYmdInTimeZone(option.period.end, timeZone),
    };
  }
  return option.type;
}

/**
 * Returns true if this date range should trigger a persist/update.
 * For custom, only requires that end is greater than or equal to start.
 */
export function shouldPersistDateRangeChange(option: TimeOption): boolean {
  if (option.type !== "custom") return true;
  const { start, end } = option.period;
  return end.getTime() >= start.getTime();
}

/**
 * Returns true if two stored date range values are equivalent (same preset or same custom start/end).
 */
export function isTimeFilterEqual(
  a: StoredDateRange | undefined,
  b: StoredDateRange | undefined,
  timeZone: string
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a === "string" && typeof b === "string") return a === b;
  if (typeof a !== "object" || typeof b !== "object") return false;
  const pa = customStoredToPeriodDates(
    a as StoredCustomDateRangeShape,
    timeZone
  );
  const pb = customStoredToPeriodDates(
    b as StoredCustomDateRangeShape,
    timeZone
  );
  if (!pa || !pb) return false;
  return (
    calendarYmdInTimeZone(pa.start, timeZone) ===
      calendarYmdInTimeZone(pb.start, timeZone) &&
    calendarYmdInTimeZone(pa.end, timeZone) ===
      calendarYmdInTimeZone(pb.end, timeZone)
  );
}

/**
 * Returns true when the stored date range should be updated (incoming differs from current).
 * Used when applying Firestore updates; ignores whether the current value was optimistic.
 */
export function shouldUpdateDate(
  incoming: StoredDateRange | undefined,
  current: StoredDateRange | undefined,
  timeZone: string
): boolean {
  return !isTimeFilterEqual(incoming, current, timeZone);
}

export interface ResolvedDateRange {
  timeOption: TimeOption;
  clampedToastMessage?: string;
  /** When start was clamped to floor, the value to persist so stored is updated. */
  persistClampedValue?: StoredDateRange;
  /** When both start and end are before floor, clear the stored value. */
  clearStored?: boolean;
}

/**
 * Resolves stored value to TimeOption. When applyClamp is true (default), custom ranges are clamped
 * to clampStart and may trigger toast/persist; when false, custom ranges are returned as-is (only
 * end >= start is enforced). Use applyClamp false when the value was set optimistically by the user.
 */
export function resolveStoredDateRange(
  stored: StoredDateRange | undefined,
  clampStart: Date,
  defaultPeriod: { start: Date; end: Date },
  applyClamp: boolean = true,
  timeZone: string
): ResolvedDateRange {
  const defaultTimeOption: TimeOption = {
    type: "custom",
    period: defaultPeriod,
  };

  if (stored === undefined || stored === null) {
    return { timeOption: defaultTimeOption };
  }

  if (typeof stored === "string") {
    return { timeOption: { type: stored } };
  }

  const period = customStoredToPeriodDates(
    stored as StoredCustomDateRangeShape,
    timeZone
  );
  if (period === undefined) {
    return { timeOption: defaultTimeOption };
  }
  const start = period.start;
  const end = period.end;

  if (!applyClamp) {
    let finalStart = start;
    let finalEnd = end;
    if (finalEnd.getTime() < finalStart.getTime()) {
      finalEnd = endOfCalendarDayContainingInstant(finalStart, timeZone);
    }
    return {
      timeOption: {
        type: "custom",
        period: { start: finalStart, end: finalEnd },
      },
    };
  }

  const clampTime = clampStart.getTime();
  if (start.getTime() < clampTime && end.getTime() < clampTime) {
    return { timeOption: defaultTimeOption, clearStored: true };
  }

  let finalStart = start;
  let wasClamped = false;
  if (start.getTime() < clampTime) {
    finalStart = clampStart;
    wasClamped = true;
  }
  let finalEnd = end;
  if (finalEnd.getTime() < finalStart.getTime()) {
    finalEnd = endOfCalendarDayContainingInstant(finalStart, timeZone);
  }

  const year = getDateTimePartsInTimeZone(finalStart, timeZone).year;
  const clampedToastMessage = wasClamped
    ? `Your date range was updated to start from January ${year}`
    : undefined;

  const result: ResolvedDateRange = {
    timeOption: {
      type: "custom",
      period: { start: finalStart, end: finalEnd },
    },
    clampedToastMessage,
  };
  if (wasClamped) {
    result.persistClampedValue = {
      start: calendarYmdInTimeZone(finalStart, timeZone),
      end: calendarYmdInTimeZone(finalEnd, timeZone),
    };
  }
  return result;
}

/**
 * Gets the default time filter for dashboards and transaction details.
 * If the current month is in Q1 (Jan, Feb, Mar), defaults to last 4 months.
 * Otherwise, defaults to current year.
 */
export function getDefaultTimeFilter(timeZone: string): TimeOption {
  const period = getDefaultDateRangePeriod(timeZone);
  return {
    type: "custom",
    period,
  };
}

/**
 * Hook for date range state and persistence by scope (dashboard | transaction).
 * Uses URL/location state when present (e.g. startDate/endDate from navigation); otherwise uses Redux.
 * Returns the resolved timeOption and setDateRange, which clears URL state (if any) then dispatches the Redux action.
 */
type AppStateLike = { system: { user?: User } };

export function useDateRange(scope: DateRangeScope) {
  const dispatch = useDispatch();
  const location = useLocation<DateRangeLocationState>();
  const history = useHistory();
  const timeZone = useSelector((state: AppStateLike) =>
    getEffectiveTimeZoneFromUser(state.system.user)
  );
  const selector =
    scope === "dashboard"
      ? dashboardDateRangeSelector
      : transactionDateRangeSelector;
  const {
    timeOption: timeOptionFromSelector,
    clampedToastMessage,
    persistClampedValue,
    clearStored,
  } = useSelector(selector);
  const lastToastRef = useRef<string | null>(null);

  const urlState = location.state;
  const hasUrlState = !!(
    urlState?.preset ||
    (urlState?.startDate && urlState?.endDate)
  );

  const timeOptionFromUrl: TimeOption | null = hasUrlState
    ? urlState!.preset
      ? { type: urlState!.preset }
      : {
          type: "custom",
          period: {
            start: new Date(urlState!.startDate!),
            end: new Date(urlState!.endDate!),
          },
        }
    : null;

  const timeOption = timeOptionFromUrl ?? timeOptionFromSelector;

  useEffect(() => {
    if (clearStored) {
      dispatch(updateDateRange(scope, undefined));
      return;
    }
    if (persistClampedValue) {
      dispatch(updateDateRange(scope, persistClampedValue));
    }
  }, [clearStored, persistClampedValue, scope, dispatch]);

  useEffect(() => {
    if (clampedToastMessage && clampedToastMessage !== lastToastRef.current) {
      lastToastRef.current = clampedToastMessage;
      toast.info(clampedToastMessage);
    }
  }, [clampedToastMessage]);

  const setDateRange = (v: TimeOption) => {
    if (urlState) {
      history.replace(location.pathname, undefined);
    }
    if (shouldPersistDateRangeChange(v)) {
      dispatch(updateDateRange(scope, toStoredDateRange(v, timeZone)));
    }
  };

  return { timeOption, setDateRange };
}
